import { useState, type FC } from 'react';
import { Button, InlineNotification, TextInput } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { formatNumber } from 'utils/number';
import { formatFeet, parseTypedNumber, toDecimalField } from './utils';
import styles from './property-details-drawer.module.scss';

export interface PropertyDetailsValues {
  groundLevelFt: number | null;
  kbElevationFt: number | null;
}

export interface PropertyDetailsDrawerProps {
  groundLevelFt: number | null;
  /** The elevation raster sampled at the surface hole; what Ground Level is seeded from. */
  demGroundLevelFt: number | null;
  kbElevationFt: number | null;
  /** True while KB is the backend's estimate rather than a recorded value. */
  kbElevationEstimated: boolean;
  saving: boolean;
  /** False until the backend serves a datum write; the drawer then reads only. */
  canSave: boolean;
  onClose: () => void;
  onSave: (values: PropertyDetailsValues) => void;
}

// Rig floors sit a few tens of feet above the dirt; outside this a datum is suspect.
const RIG_HEIGHT_MIN_FT = 10;
const RIG_HEIGHT_MAX_FT = 45;
// A ground level this far from the DEM usually means a different vertical datum, or a
// surface location that does not match the pad.
const DEM_TOLERANCE_FT = 50;

/**
 * Whether a typed elevation is something other than a number. Blank is allowed: an
 * elevation that is not on record is not an elevation at sea level.
 * @param {string} raw The field as typed.
 * @return {boolean} True when the entry cannot be read as a number.
 */
const isInvalid = (raw: string): boolean => {
  const trimmed = raw.trim();
  return trimmed !== '' && Number.isNaN(Number(trimmed.replace(/,/g, '')));
};

/**
 * Story 5's Property Details drawer: the ground level (seeded from the DEM) and Kelly
 * Bushing every depth is referenced to, with the derived rig height as a sanity check.
 * Its warnings never block save, so a bad vendor record can always be corrected.
 * @param {PropertyDetailsDrawerProps} props The current elevations, the DEM sample, and save.
 * @return {JSX.Element} The drawer.
 */
export const PropertyDetailsDrawer: FC<PropertyDetailsDrawerProps> = ({
  groundLevelFt,
  demGroundLevelFt,
  kbElevationFt,
  kbElevationEstimated,
  saving,
  canSave,
  onClose,
  onSave,
}) => {
  // Seeded at two decimals: the DEM sample carries fourteen, which reads as noise.
  const [groundLevel, setGroundLevel] = useState(
    toDecimalField(groundLevelFt, 2)
  );
  const [kellyBushing, setKellyBushing] = useState(
    kbElevationEstimated ? '' : toDecimalField(kbElevationFt, 2)
  );

  const gl = parseTypedNumber(groundLevel);
  const kb = parseTypedNumber(kellyBushing);
  const groundInvalid = isInvalid(groundLevel);
  const kbInvalid = isInvalid(kellyBushing);
  // A tenth of a foot: the rig height is a difference of two filed elevations, and
  // rounding it to whole feet hides a disagreement the operator should see.
  const rigHeight =
    gl !== null && kb !== null ? Math.round((kb - gl) * 10) / 10 : null;
  const kbBelowGround = rigHeight !== null && rigHeight < 0;
  const rigHeightOdd =
    rigHeight !== null &&
    !kbBelowGround &&
    (rigHeight < RIG_HEIGHT_MIN_FT || rigHeight > RIG_HEIGHT_MAX_FT);
  const demDelta =
    gl !== null && demGroundLevelFt !== null ? gl - demGroundLevelFt : null;
  const farFromDem = demDelta !== null && Math.abs(demDelta) > DEM_TOLERANCE_FT;

  // What every TVDSS in the workspace is referenced to: KB when set, else ground level.
  const datum =
    kb !== null
      ? { value: kb, label: 'KB' }
      : gl !== null
        ? { value: gl, label: 'ground level' }
        : null;

  return (
    <div className={styles.panel} aria-label="Property Details">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Property Details</h2>
          <p className={styles.subtitle}>
            Set the elevations this wellbore’s depths are referenced to.
          </p>
        </div>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close property details"
          onClick={onClose}
        />
      </div>

      <div className={styles.body}>
        <TextInput
          id="property-details-ground-level"
          labelText={
            <>
              Ground Level Elevation
              {demGroundLevelFt !== null && (
                <span className={styles.demValue}>
                  {' '}
                  (DEM value = {formatNumber(demGroundLevelFt, 0, 0)} ft)
                </span>
              )}
            </>
          }
          helperText="Feet above sea level — the surface dirt at the pad"
          inputMode="decimal"
          value={groundLevel}
          disabled={saving}
          invalid={groundInvalid}
          invalidText="Enter a valid number"
          onChange={(event) => setGroundLevel(event.target.value)}
        />
        <TextInput
          id="property-details-kelly-bushing"
          labelText="Kelly Bushing Elevation"
          helperText="Feet above sea level — the rotary table MD is zeroed from"
          placeholder={
            kbElevationEstimated && kbElevationFt !== null
              ? `Estimated ${formatNumber(kbElevationFt, 0, 0)}`
              : undefined
          }
          inputMode="decimal"
          value={kellyBushing}
          disabled={saving}
          invalid={kbInvalid}
          invalidText="Enter a valid number"
          onChange={(event) => setKellyBushing(event.target.value)}
        />

        <div className={styles.derived}>
          <span className={styles.derivedLabel}>Estimated Rig Height</span>
          <span className={styles.derivedValue}>
            {rigHeight === null ? '—' : `${formatNumber(rigHeight, 0, 1)} ft`}
          </span>
          <span className={styles.derivedHint}>
            Kelly Bushing minus ground level
          </span>
        </div>

        {datum ? (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            className={styles.notice}
            title={`Depths reference ${formatNumber(datum.value, 0, 0)} ft (${
              datum.label
            }).`}
            subtitle="TVDSS = datum − TVD. Changing it re-references every subsea depth this wellbore reports."
          />
        ) : (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            className={styles.notice}
            title="No datum on record."
            subtitle="Without a ground level or Kelly Bushing there is nothing to convert TVD to TVDSS through, so the two are entered independently."
          />
        )}
        {kbBelowGround && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={styles.notice}
            title="Kelly Bushing is below ground level."
            subtitle="The rotary table cannot sit under the dirt. One of the two elevations is wrong, or they have been entered the other way around."
          />
        )}
        {rigHeightOdd && rigHeight !== null && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={styles.notice}
            title={`Estimated rig height is ${formatNumber(
              rigHeight,
              0,
              0
            )} ft.`}
            subtitle={`A rig floor normally sits ${RIG_HEIGHT_MIN_FT}–${RIG_HEIGHT_MAX_FT} ft above ground level. Check both elevations — a height outside that range usually means one is referenced to a different datum.`}
          />
        )}
        {farFromDem && demDelta !== null && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={styles.notice}
            title={`Ground level is ${formatNumber(
              Math.abs(demDelta),
              0,
              0
            )} ft ${demDelta > 0 ? 'above' : 'below'} the DEM reading.`}
            subtitle={`Insights sampled ${formatFeet(
              demGroundLevelFt
            )} from the elevation raster at this location. A gap this large is usually a different vertical datum or a surface location that does not match the pad.`}
          />
        )}
        {!canSave && (
          <p className={styles.readOnlyNote}>
            Elevations can be read here but not yet saved from this workspace.
          </p>
        )}
      </div>

      <div className={styles.footer}>
        <Button kind="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          kind="primary"
          // Two blank boxes would post an empty body, which the backend refuses.
          disabled={
            !canSave ||
            saving ||
            groundInvalid ||
            kbInvalid ||
            (gl === null && kb === null)
          }
          onClick={() => onSave({ groundLevelFt: gl, kbElevationFt: kb })}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};
